/**
 * What this browser reports about itself, sent with the sign-in so the backend's login
 * events (`freighai_central.login_events`) can describe the device fully — the parts no
 * request header carries: time zone, screen and, on Chrome / Edge / Brave, the real
 * Windows or Android version and the phone model.
 *
 * Best-effort throughout: every field is optional, nothing here throws, and the one
 * asynchronous read is capped so a sign-in is never held up by it.
 */

interface UserAgentDataValues {
  platform?: string
  platformVersion?: string
  architecture?: string
  bitness?: string
  model?: string
  mobile?: boolean
  fullVersionList?: { brand: string; version: string }[]
}

interface NavigatorUserAgentData {
  platform?: string
  mobile?: boolean
  getHighEntropyValues?: (hints: string[]) => Promise<UserAgentDataValues>
}

type ExtendedNavigator = Navigator & {
  userAgentData?: NavigatorUserAgentData
  deviceMemory?: number
}

const HIGH_ENTROPY_TIMEOUT_MS = 300

export async function collectClientDetails(): Promise<Record<string, unknown>> {
  const nav = navigator as ExtendedNavigator
  const details: Record<string, unknown> = {}

  try {
    details.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    details.utcOffsetMinutes = -new Date().getTimezoneOffset()
    details.language = nav.language
    details.languages = nav.languages ? [...nav.languages].slice(0, 10) : undefined
    details.screenWidth = window.screen?.width
    details.screenHeight = window.screen?.height
    details.viewportWidth = window.innerWidth
    details.viewportHeight = window.innerHeight
    details.pixelRatio = window.devicePixelRatio
    details.colorDepth = window.screen?.colorDepth
    details.maxTouchPoints = nav.maxTouchPoints
    details.hardwareConcurrency = nav.hardwareConcurrency
    details.deviceMemoryGb = nav.deviceMemory
    details.cookiesEnabled = nav.cookieEnabled
  } catch {
    // best effort
  }

  const uaData = nav.userAgentData
  if (uaData) {
    details.platform = uaData.platform
    details.mobile = uaData.mobile
    try {
      const values = await Promise.race([
        uaData.getHighEntropyValues?.([
          'platformVersion',
          'architecture',
          'bitness',
          'model',
          'fullVersionList',
        ]),
        new Promise<undefined>((resolve) => setTimeout(resolve, HIGH_ENTROPY_TIMEOUT_MS)),
      ])
      if (values) {
        details.platformVersion = values.platformVersion
        details.architecture = values.architecture
        details.bitness = values.bitness
        details.model = values.model || undefined
        const brands = values.fullVersionList ?? []
        const named =
          brands.find((b) => !/not.?a.?brand/i.test(b.brand) && b.brand !== 'Chromium') ??
          brands.find((b) => b.brand === 'Chromium')
        details.browserFullVersion = named?.version
      }
    } catch {
      // best effort
    }
  }

  return details
}
